import { useMemo, useState } from "react";
import DossierSlide, { LAYOUT_OPTIONS } from "@/components/dossier/DossierSlide";

/**
 * Modal con galería visual de TODAS las plantillas aplicadas al contenido
 * actual de la sección. Cada miniatura es un `DossierSlide` real renderizado
 * a tamaño thumbnail (container queries `cqw` lo hace responsive solo).
 *
 * Cuando el usuario hace click en una miniatura, se aplica ese layout y se
 * cierra el modal. Mucho más rápido que el dropdown de texto cuando estás
 * explorando qué plantilla queda mejor para la sección.
 *
 * Vive a nivel "componente" para poder reutilizarse desde SlidePreview o
 * desde el panel de presentación si quisiéramos.
 */
export default function LayoutGalleryModal({
  open,
  onClose,
  section,
  project,
  currentLayout,
  onPickLayout,
}) {
  const [query, setQuery] = useState("");

  // Filtrar la lista: omitimos "Automático" (string vacío) porque ya está
  // implícito en el resto. Si hay query, hacemos un filtro fuzzy por label.
  const options = useMemo(() => {
    const base = LAYOUT_OPTIONS.filter((o) => o.value !== "");
    const q = query.trim().toLowerCase();
    if (!q) return base;
    return base.filter((o) => o.label.toLowerCase().includes(q));
  }, [query]);

  if (!open) return null;

  const handlePick = (value) => {
    onPickLayout?.(value);
    onClose?.();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
      onClick={onClose}
      data-testid="layout-gallery-modal"
    >
      <div
        className="relative flex h-[88vh] w-[1200px] max-w-full flex-col overflow-hidden rounded-xl bg-[#FAFAF6] shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <header className="flex items-center justify-between gap-4 border-b border-stone-200 bg-white px-5 py-3">
          <div className="min-w-0">
            <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-[#941620]">
              Galería de plantillas
            </p>
            <p className="mt-0.5 truncate font-serif text-base text-stone-900">
              Sección: <strong>{section?.label || "—"}</strong>
            </p>
          </div>
          <div className="flex items-center gap-2">
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar plantilla…"
              data-testid="layout-gallery-search"
              className="w-48 rounded-full border border-stone-300 bg-white px-3 py-1 text-xs outline-none focus:border-[#941620]"
            />
            <button
              onClick={onClose}
              data-testid="layout-gallery-close"
              className="rounded-full border border-stone-300 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.22em] text-stone-700 hover:border-[#941620] hover:text-[#941620]"
            >
              ✕ Cerrar
            </button>
          </div>
        </header>

        {/* Subheader: pista de "Automático" */}
        <div className="border-b border-stone-200 bg-stone-50 px-5 py-2">
          <button
            onClick={() => handlePick("")}
            data-testid="layout-gallery-auto"
            className={`flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] transition ${
              currentLayout === "" || currentLayout == null
                ? "font-bold text-[#26775E]"
                : "text-stone-600 hover:text-[#26775E]"
            }`}
          >
            <span
              className="inline-block h-1.5 w-1.5 rounded-full"
              style={{ background: currentLayout === "" ? "#26775E" : "#a8a29e" }}
            />
            Automático (que el sistema elija por el contenido)
          </button>
        </div>

        {/* Grid de miniaturas. 3 columnas en desktop. Container queries hacen
            que cada DossierSlide se escale automáticamente a su contenedor. */}
        <div className="grid flex-1 grid-cols-2 gap-3 overflow-y-auto p-5 md:grid-cols-3">
          {options.length === 0 && (
            <div className="col-span-full rounded-lg border border-dashed border-stone-300 p-8 text-center text-sm text-stone-500">
              Sin coincidencias para "{query}".
            </div>
          )}
          {options.map((opt) => {
            const active = opt.value === currentLayout;
            return (
              <button
                key={opt.value}
                onClick={() => handlePick(opt.value)}
                data-testid={`layout-gallery-pick-${opt.value}`}
                className={`group overflow-hidden rounded-lg border-2 bg-white text-left transition ${
                  active
                    ? "border-[#941620] shadow-lg"
                    : "border-stone-200 hover:border-[#941620]/60 hover:shadow-md"
                }`}
              >
                <div
                  className="relative w-full bg-[#2C2B29]"
                  style={{ aspectRatio: "16 / 9", containerType: "inline-size" }}
                >
                  <div className="absolute inset-0">
                    <DossierSlide
                      section={section}
                      project={project}
                      layout={opt.value}
                      showWatermark={false}
                    />
                  </div>
                  {/* Overlay sutil al hover para señalar interactividad */}
                  <div className="absolute inset-0 bg-[#941620]/0 transition group-hover:bg-[#941620]/8" />
                </div>
                <div className="flex items-center justify-between gap-2 border-t border-stone-200 px-3 py-2">
                  <p className={`truncate text-[11px] font-semibold uppercase tracking-[0.2em] ${active ? "text-[#941620]" : "text-stone-700"}`}>
                    {opt.label}
                  </p>
                  {active && (
                    <span
                      className="shrink-0 rounded-full bg-[#941620] px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.18em] text-white"
                      data-testid="layout-gallery-active-tag"
                    >
                      Activa
                    </span>
                  )}
                </div>
              </button>
            );
          })}
        </div>

        {/* Footer */}
        <footer className="border-t border-stone-200 bg-stone-50 px-5 py-2 text-[10px] uppercase tracking-[0.22em] text-stone-500">
          {options.length} plantilla{options.length === 1 ? "" : "s"} · click en una miniatura para aplicarla
        </footer>
      </div>
    </div>
  );
}
